
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { useProject } from "@/hooks/useProject";
import { ProjectHeader } from "@/components/project/ProjectHeader";
import { ProjectDetailsTab } from "@/components/project/ProjectDetailsTab";
import { PricingTab } from "@/components/project/PricingTab";
import { InvoiceSettingsTab } from "@/components/project/InvoiceSettingsTab";
import { ProjectTeamSection } from "@/components/teams/ProjectTeamSection";
import { InvoiceSettings } from "@/types/Project";

const projectFormSchema = z.object({
  name: z.string().min(1, "Project name is required"),
  description: z.string().optional(),
  hourlyRate: z.number().min(0, "Hourly rate must be positive"),
  isArchived: z.boolean(),
  pricingComponents: z.any(), // We'll keep this as any for now
  invoiceSettings: z.object({
    clientName: z.string().optional(),
    clientEmail: z.string().email("Invalid email address").optional().or(z.literal("")),
    clientAddress: z.string().optional(),
    paymentTerms: z.string().optional(),
    paymentMethods: z.string().optional(),
    bankDetails: z.string().optional(),
    notes: z.string().optional(),
  }).optional(),
});

type ProjectFormValues = z.infer<typeof projectFormSchema>;

export default function ProjectDetail() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { project, isLoading, updateProject, deleteProject } = useProject();
  
  const form = useForm<ProjectFormValues>({
    resolver: zodResolver(projectFormSchema),
    defaultValues: {
      name: "",
      description: "",
      hourlyRate: 0,
      isArchived: false,
      pricingComponents: [],
      invoiceSettings: {
        clientName: "",
        clientEmail: "",
        clientAddress: "",
        paymentTerms: "Net 30",
        paymentMethods: "",
        bankDetails: "",
        notes: ""
      }
    }
  });

  // Initialize form values when project loads
  useEffect(() => {
    if (project) {
      form.reset({
        name: project.name,
        description: project.description || "",
        hourlyRate: project.hourlyRate,
        isArchived: project.isArchived,
        pricingComponents: project.pricingComponents || [],
        invoiceSettings: project.invoiceSettings || {
          clientName: "",
          clientEmail: "",
          clientAddress: "",
          paymentTerms: "Net 30",
          paymentMethods: "",
          bankDetails: "",
          notes: ""
        }
      });
    }
  }, [project, form.reset]);

  const handleUpdateProject = async (data: ProjectFormValues) => {
    try {
      if (!project?.id) throw new Error("Project ID is required");

      const hasInvoiceSettings = data.invoiceSettings && 
        Object.values(data.invoiceSettings).some(val => !!val);

      await updateProject({
        name: data.name,
        description: data.description,
        hourly_rate: data.hourlyRate,
        is_archived: data.isArchived,
        pricing_components: data.pricingComponents ? JSON.stringify(data.pricingComponents) : null,
        invoice_settings: hasInvoiceSettings ? JSON.stringify(data.invoiceSettings) : null,
      });
      
      toast({
        title: "Success",
        description: "Project updated successfully",
      });
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to update project",
        variant: "destructive",
      });
    }
  };

  const handleDeleteProject = async () => {
    try {
      if (!project?.id) throw new Error("Project ID is required");
      
      if (confirm("Are you sure you want to delete this project? This action cannot be undone.")) {
        await deleteProject(project.id);
        navigate("/projects");
      }
    } catch (error) {
      console.error(error);
      toast({
        title: "Error",
        description: "Failed to delete project",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <div>Loading project details...</div>;
  }

  if (!project) {
    return <div>Project not found</div>;
  }

  return (
    <div className="w-full px-4 py-8 mx-auto max-w-3xl">
      <ProjectHeader name={form.watch("name")} isArchived={form.watch("isArchived")} />

      <FormProvider {...form}>
        <form onSubmit={form.handleSubmit(handleUpdateProject)}>
          <Tabs defaultValue="details" className="w-full">
            <TabsList className="grid grid-cols-4 mb-4">
              <TabsTrigger value="details">Project Details</TabsTrigger>
              <TabsTrigger value="pricing">Pricing</TabsTrigger>
              <TabsTrigger value="invoice">Invoice Settings</TabsTrigger>
              <TabsTrigger value="team">Team</TabsTrigger>
            </TabsList>

            <TabsContent value="details">
              <ProjectDetailsTab />
            </TabsContent>

            <TabsContent value="pricing">
              <PricingTab 
                pricingComponents={form.watch("pricingComponents")}
                setPricingComponents={(components) => {
                  form.setValue("pricingComponents", components, { shouldValidate: true });
                }}
              />
            </TabsContent>

            <TabsContent value="invoice">
              <InvoiceSettingsTab />
            </TabsContent>
            
            <TabsContent value="team">
              <ProjectTeamSection project={project} />
            </TabsContent>
          </Tabs>

          <div className="flex justify-end space-x-2 mt-4">
            <Button 
              type="button" 
              variant="destructive" 
              onClick={handleDeleteProject}
            >
              Delete Project
            </Button>
            <Button type="submit">Update Project</Button>
          </div>
        </form>
      </FormProvider>
    </div>
  );
}
